import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config';
import { Plus, Trash2, Edit, Upload, X, Save, Loader2 } from 'lucide-react';

const HEADERS = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`
});

export default function LookupDatasetsAdmin() {
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editingDataset, setEditingDataset] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => { loadDatasets(); }, []);

  const loadDatasets = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/lookup/datasets`, { headers: HEADERS() });
      const data = r.ok ? await r.json() : [];
      setDatasets(data);
    } finally { setLoading(false); }
  };

  const handleDeleteDataset = async (id) => {
    if (!window.confirm('Delete this dataset?')) return;
    await fetch(`${API_BASE_URL}/api/lookup/datasets/${encodeURIComponent(id)}`, { method: 'DELETE', headers: HEADERS() });
    if (selected && selected.id === id) setSelected(null);
    loadDatasets();
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0 }}>Lookup Datasets</h1>
          <div style={{ color: '#64748b', marginTop: 4 }}>Master data used by form fields for fuzzy lookup and auto-fed by approved requests.</div>
        </div>
        <button className="button-primary" onClick={() => { setCreating(true); setEditingDataset(null); }}>
          <Plus size={16} style={{ marginRight: 4 }} /> New Dataset
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Loader2 size={32} className="animate-spin" /></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20 }}>
          <div style={{ background: '#f9fafb', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, height: 'fit-content' }}>
            {datasets.length === 0 && <div style={{ color: '#64748b', padding: 12 }}>No datasets yet</div>}
            {datasets.map(d => (
              <div
                key={d.id}
                onClick={() => { setSelected(d); setEditingDataset(null); setCreating(false); }}
                style={{
                  padding: 10, marginBottom: 6, borderRadius: 6,
                  cursor: 'pointer',
                  background: selected?.id === d.id ? '#dbeafe' : 'white',
                  border: '1px solid #e2e8f0'
                }}
              >
                <div style={{ fontWeight: 600 }}>{d.name}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{d.id}</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{d.searchFields.join(', ')}</div>
                <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                  <button
                    className="button-outline"
                    style={{ fontSize: 11, padding: '2px 6px' }}
                    onClick={(e) => { e.stopPropagation(); setEditingDataset(d); setSelected(d); setCreating(false); }}
                  >
                    <Edit size={12} /> Edit
                  </button>
                  <button
                    className="button-outline"
                    style={{ fontSize: 11, padding: '2px 6px', color: '#dc2626' }}
                    onClick={(e) => { e.stopPropagation(); handleDeleteDataset(d.id); }}
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div>
            {creating && (
              <DatasetEditor
                mode="create"
                onCancel={() => setCreating(false)}
                onSaved={(d) => { setCreating(false); loadDatasets(); setSelected(d); }}
              />
            )}
            {editingDataset && (
              <DatasetEditor
                mode="edit"
                initial={editingDataset}
                onCancel={() => setEditingDataset(null)}
                onSaved={(d) => { setEditingDataset(null); loadDatasets(); setSelected(d); }}
              />
            )}
            {!creating && !editingDataset && selected && (
              <EntriesPanel dataset={selected} onReload={loadDatasets} />
            )}
            {!creating && !editingDataset && !selected && (
              <div style={{ color: '#64748b', padding: 40, textAlign: 'center', border: '1px dashed #cbd5e1', borderRadius: 8 }}>
                Select a dataset on the left or create a new one
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DatasetEditor({ mode, initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || {
    id: '', name: '', description: '', valueField: 'code',
    searchFields: ['description'],
    displayLabelTemplate: '{{description}}',
    metadataSchema: [{ key: 'code', label: 'Code' }, { key: 'createdDate', label: 'Created', format: 'date' }],
    isActive: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const updateField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setError(null); setSaving(true);
    try {
      const url = mode === 'create'
        ? `${API_BASE_URL}/api/lookup/datasets`
        : `${API_BASE_URL}/api/lookup/datasets/${encodeURIComponent(form.id)}`;
      const method = mode === 'create' ? 'POST' : 'PUT';
      const r = await fetch(url, { method, headers: HEADERS(), body: JSON.stringify(form) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setError(d.message || 'Save failed'); return; }
      const saved = await r.json();
      onSaved(saved);
    } finally { setSaving(false); }
  };

  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>{mode === 'create' ? 'New Dataset' : `Edit ${initial.name}`}</h3>
        <button className="button-outline" onClick={onCancel}><X size={14} /> Cancel</button>
      </div>
      {error && <div style={{ background: '#fef2f2', color: '#dc2626', padding: 8, borderRadius: 6, marginBottom: 10 }}>{error}</div>}
      <Row label="ID (slug)"><input className="form-field-input" value={form.id} disabled={mode === 'edit'} onChange={(e) => updateField('id', e.target.value)} placeholder="material_codes" /></Row>
      <Row label="Name"><input className="form-field-input" value={form.name} onChange={(e) => updateField('name', e.target.value)} placeholder="Material Codes" /></Row>
      <Row label="Description"><input className="form-field-input" value={form.description || ''} onChange={(e) => updateField('description', e.target.value)} /></Row>
      <Row label="Value field"><input className="form-field-input" value={form.valueField} onChange={(e) => updateField('valueField', e.target.value)} placeholder="code" /></Row>
      <Row label="Search fields (comma)"><input className="form-field-input" value={(form.searchFields || []).join(', ')} onChange={(e) => updateField('searchFields', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="description" /></Row>
      <Row label="Display label template"><input className="form-field-input" value={form.displayLabelTemplate || ''} onChange={(e) => updateField('displayLabelTemplate', e.target.value)} placeholder="{{description}}" /></Row>
      <Row label="Metadata columns">
        <MetadataSchemaEditor value={form.metadataSchema || []} onChange={(v) => updateField('metadataSchema', v)} />
      </Row>
      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <button className="button-primary" onClick={handleSave} disabled={saving}><Save size={14} /> {saving ? 'Saving…' : 'Save Dataset'}</button>
      </div>
    </div>
  );
}

function MetadataSchemaEditor({ value, onChange }) {
  const update = (i, patch) => {
    const next = value.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...value, { key: '', label: '', format: 'text' }]);
  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 8 }}>
      {value.map((col, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 110px 30px', gap: 6, marginBottom: 6 }}>
          <input className="form-field-input" placeholder="key" value={col.key} onChange={(e) => update(i, { key: e.target.value })} />
          <input className="form-field-input" placeholder="label" value={col.label || ''} onChange={(e) => update(i, { label: e.target.value })} />
          <select className="form-field-input" value={col.format || 'text'} onChange={(e) => update(i, { format: e.target.value })}>
            <option value="text">text</option>
            <option value="date">date</option>
            <option value="datetime">datetime</option>
          </select>
          <button className="button-outline" onClick={() => remove(i)} style={{ padding: '4px 6px' }}><Trash2 size={14} /></button>
        </div>
      ))}
      <button className="button-outline" onClick={add} style={{ marginTop: 4 }}><Plus size={14} /> Add column</button>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 12, marginBottom: 10, alignItems: 'center' }}>
      <label style={{ color: '#475569', fontSize: 13 }}>{label}</label>
      <div>{children}</div>
    </div>
  );
}

function EntriesPanel({ dataset, onReload }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  const load = async (p = page, query = q) => {
    setLoading(true);
    try {
      const url = `${API_BASE_URL}/api/lookup/datasets/${encodeURIComponent(dataset.id)}/entries/all?page=${p}&pageSize=25&q=${encodeURIComponent(query)}`;
      const r = await fetch(url, { headers: HEADERS() });
      const data = r.ok ? await r.json() : { items: [], total: 0 };
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(p);
    } finally { setLoading(false); }
  };

  useEffect(() => { setQ(''); load(1, ''); }, [dataset.id]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    await fetch(`${API_BASE_URL}/api/lookup/datasets/${encodeURIComponent(dataset.id)}/entries/${id}`, { method: 'DELETE', headers: HEADERS() });
    load();
  };

  const handleImport = async (file) => {
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_BASE_URL}/api/lookup/datasets/${encodeURIComponent(dataset.id)}/entries/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: fd
      });
      const data = await r.json().catch(() => ({}));
      setImportResult(data);
      load(1, q);
    } finally { setImporting(false); }
  };

  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: 0 }}>{dataset.name}</h3>
          <div style={{ fontSize: 13, color: '#64748b' }}>{total} entries</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImport(f); e.target.value = ''; }}
          />
          <button className="button-outline" onClick={() => fileRef.current?.click()} disabled={importing}>
            <Upload size={14} /> {importing ? 'Importing…' : 'Import CSV'}
          </button>
          <button className="button-primary" onClick={() => setEditing({ data: {}, isNew: true })}>
            <Plus size={14} /> New Entry
          </button>
        </div>
      </div>

      {importResult && (
        <div style={{
          marginBottom: 12, padding: 10, borderRadius: 6,
          background: importResult.errors?.length ? '#fef3c7' : '#dcfce7',
          color: '#065f46', fontSize: 13
        }}>
          Imported: {importResult.inserted} inserted, {importResult.updated} updated
          {importResult.errors?.length > 0 && `, ${importResult.errors.length} errors`}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input
          className="form-field-input"
          placeholder="Filter entries…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') load(1, q); }}
        />
        <button className="button-outline" onClick={() => load(1, q)}>Search</button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><Loader2 size={24} className="animate-spin" /></div>
      ) : (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {dataset.searchFields.concat([dataset.valueField]).filter((v, i, a) => a.indexOf(v) === i).map(k => (
                  <th key={k} style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e2e8f0' }}>{k}</th>
                ))}
                <th style={{ textAlign: 'right', padding: 8, borderBottom: '1px solid #e2e8f0', width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  {dataset.searchFields.concat([dataset.valueField]).filter((v, i, a) => a.indexOf(v) === i).map(k => (
                    <td key={k} style={{ padding: 8 }}>{String(it.data[k] ?? '')}</td>
                  ))}
                  <td style={{ padding: 8, textAlign: 'right' }}>
                    <button className="button-outline" style={{ padding: '2px 6px', fontSize: 11 }} onClick={() => setEditing({ ...it, isNew: false })}><Edit size={12} /></button>
                    <button className="button-outline" style={{ padding: '2px 6px', fontSize: 11, marginLeft: 4, color: '#dc2626' }} onClick={() => handleDelete(it.id)}><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={10} style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>No entries</td></tr>
              )}
            </tbody>
          </table>

          {total > 25 && (
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center', gap: 8 }}>
              <button className="button-outline" disabled={page <= 1} onClick={() => load(page - 1, q)}>Prev</button>
              <span style={{ alignSelf: 'center' }}>Page {page} of {Math.ceil(total / 25)}</span>
              <button className="button-outline" disabled={page >= Math.ceil(total / 25)} onClick={() => load(page + 1, q)}>Next</button>
            </div>
          )}
        </>
      )}

      {editing && (
        <EntryEditor
          dataset={dataset}
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(page, q); }}
        />
      )}
    </div>
  );
}

function EntryEditor({ dataset, entry, onClose, onSaved }) {
  const initialKeys = [
    ...new Set([dataset.valueField, ...(dataset.searchFields || []), ...((dataset.metadataSchema || []).map(m => m.key))].filter(Boolean))
  ];
  const initialData = { ...entry.data };
  initialKeys.forEach(k => { if (!(k in initialData)) initialData[k] = ''; });
  const [data, setData] = useState(initialData);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      const url = entry.isNew
        ? `${API_BASE_URL}/api/lookup/datasets/${encodeURIComponent(dataset.id)}/entries`
        : `${API_BASE_URL}/api/lookup/datasets/${encodeURIComponent(dataset.id)}/entries/${entry.id}`;
      const method = entry.isNew ? 'POST' : 'PUT';
      const r = await fetch(url, { method, headers: HEADERS(), body: JSON.stringify({ data }) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setError(d.message || 'Save failed'); return; }
      onSaved();
    } finally { setSaving(false); }
  };

  const allKeys = [...new Set([...initialKeys, ...Object.keys(data)])];

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{ background: 'white', borderRadius: 8, padding: 20, width: 520, maxHeight: '80vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{entry.isNew ? 'New Entry' : `Edit Entry #${entry.id}`}</h3>
          <button className="button-outline" onClick={onClose}><X size={14} /></button>
        </div>
        {error && <div style={{ background: '#fef2f2', color: '#dc2626', padding: 8, borderRadius: 6, marginBottom: 10 }}>{error}</div>}
        {allKeys.map(k => (
          <Row key={k} label={k}>
            <input className="form-field-input" value={data[k] ?? ''} onChange={(e) => setData(prev => ({ ...prev, [k]: e.target.value }))} />
          </Row>
        ))}
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="button-outline" onClick={onClose}>Cancel</button>
          <button className="button-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
